import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useT } from "@/i18n";

const FIELDS = [
  ["firm_name", "settings.firm_name", "settings-firm-name"],
  ["company_code", "settings.company_code", "settings-company-code"],
  ["vat_number", "settings.vat_number", "settings-vat-number"],
  ["address", "settings.address", "settings-address"],
  ["telephone", "settings.telephone", "settings-telephone"],
  ["email", "settings.email", "settings-email"],
  ["bank_name", "settings.bank_name", "settings-bank-name"],
  ["swift", "settings.swift", "settings-swift"],
  ["iban1", "settings.iban1", "settings-iban1"],
  ["iban2", "settings.iban2", "settings-iban2"],
];

const INTEGRATION_DEFAULTS = {
  everything_fields: ["client", "name", "internal_ref", "court_case_number"],
  obsidian_default_vault: "",
  calendar_default_reminder: "none",
  default_ui_language: "lt",
};

export default function Settings() {
  const { t } = useT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoVer, setLogoVer] = useState(Date.now());
  const [logoFiles, setLogoFiles] = useState({});
  const [logoBusy, setLogoBusy] = useState("");

  const uploadLogo = async (kind) => {
    const file = logoFiles[kind];
    if (!file) return;
    setLogoBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/settings/logo?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("settings.logo_uploaded"));
      setLogoFiles((f) => ({ ...f, [kind]: null }));
      setLogoVer(Date.now());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLogoBusy("");
    }
  };

  useEffect(() => {
    api.get("/settings")
      .then((r) => setForm({ ...INTEGRATION_DEFAULTS, ...r.data }))
      .catch(() => {});
  }, []);

  if (!form) {
    return <div className="text-sm text-slate-500" data-testid="settings-loading">{t("settings.loading")}</div>;
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_vat_rate: Number(form.default_vat_rate) || 0,
        default_payment_term_days: parseInt(form.default_payment_term_days, 10) || 0,
        next_invoice_number: parseInt(form.next_invoice_number, 10) || 1,
        receipt_series: form.receipt_series || "PPK",
        next_receipt_number: parseInt(form.next_receipt_number, 10) || 1,
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{t("settings.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">
          {t("settings.subtitle")}
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-lg p-6 space-y-6 shadow-sm"
        data-testid="settings-form"
      >
        <div>
          <h2 className="font-serif text-lg text-slate-800 mb-4">{t("settings.firm")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map(([key, label, testid]) => (
              <div key={key} className={["firm_name", "address"].includes(key) ? "sm:col-span-2" : ""}>
                <Label htmlFor={`set-${key}`}>{t(label)}</Label>
                <Input
                  id={`set-${key}`}
                  data-testid={testid}
                  value={form[key] ?? ""}
                  onChange={set(key)}
                  className="mt-1.5"
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-serif text-lg text-slate-800 mb-4">{t("settings.invoice_defaults")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="set-vat">{t("settings.default_vat")}</Label>
              <Input
                id="set-vat"
                data-testid="settings-default-vat-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.default_vat_rate}
                onChange={set("default_vat_rate")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="set-term">{t("settings.payment_term")}</Label>
              <Input
                id="set-term"
                data-testid="settings-payment-term"
                type="number"
                min="0"
                step="1"
                value={form.default_payment_term_days}
                onChange={set("default_payment_term_days")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="set-prefix">{t("settings.invoice_prefix")}</Label>
              <Input
                id="set-prefix"
                data-testid="settings-invoice-prefix"
                value={form.invoice_prefix}
                onChange={set("invoice_prefix")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="set-next">{t("settings.next_number")}</Label>
              <Input
                id="set-next"
                data-testid="settings-next-invoice-number"
                type="number"
                min="1"
                step="1"
                value={form.next_invoice_number}
                onChange={set("next_invoice_number")}
                className="mt-1.5"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                {t("settings.next_suggested", {
                  number: `${form.invoice_prefix}${String(parseInt(form.next_invoice_number, 10) || 1).padStart(6, "0")}`,
                })}
              </p>
            </div>
            <div>
              <Label htmlFor="set-receipt-series">{t("settings.receipt_series")}</Label>
              <Input
                id="set-receipt-series"
                data-testid="settings-receipt-series"
                value={form.receipt_series || ""}
                onChange={set("receipt_series")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="set-receipt-next">{t("settings.next_receipt_number")}</Label>
              <Input
                id="set-receipt-next"
                data-testid="settings-next-receipt-number"
                type="number"
                min="1"
                step="1"
                value={form.next_receipt_number || 1}
                onChange={set("next_receipt_number")}
                className="mt-1.5"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                {t("settings.next_suggested", {
                  number: `${form.receipt_series || "PPK"}${String(parseInt(form.next_receipt_number, 10) || 1).padStart(3, "0")}`,
                })}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-serif text-lg text-slate-800 mb-4">{t("settings.integrations")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>{t("settings.everything_fields")}</Label>
              <div className="mt-2 flex flex-wrap gap-4">
                {[
                  ["client", "settings.ef_client"],
                  ["name", "settings.ef_name"],
                  ["internal_ref", "settings.ef_internal_ref"],
                  ["court_case_number", "settings.ef_case_number"],
                ].map(([f, key]) => (
                  <label key={f} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`settings-ef-${f}`}>
                    <input
                      type="checkbox"
                      className="accent-slate-800"
                      data-testid={`settings-ef-checkbox-${f}`}
                      checked={(form.everything_fields || []).includes(f)}
                      onChange={(e) =>
                        setForm((cur) => ({
                          ...cur,
                          everything_fields: e.target.checked
                            ? [...(cur.everything_fields || []), f]
                            : (cur.everything_fields || []).filter((x) => x !== f),
                        }))
                      }
                    />
                    {t(key)}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="set-obsidian">{t("settings.obsidian_vault")}</Label>
              <Input
                id="set-obsidian"
                data-testid="settings-obsidian-vault"
                value={form.obsidian_default_vault || ""}
                onChange={set("obsidian_default_vault")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{t("settings.calendar_reminder")}</Label>
              <Select
                value={form.calendar_default_reminder || "none"}
                onValueChange={set("calendar_default_reminder")}
              >
                <SelectTrigger data-testid="settings-calendar-reminder" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="settings-calendar-reminder-none">
                    {t("cal.reminder_none")}
                  </SelectItem>
                  {["1", "3", "7", "14"].map((r) => (
                    <SelectItem key={r} value={r} data-testid={`settings-calendar-reminder-${r}`}>
                      {r === "1" ? t("cal.reminder_day") : t("cal.reminder_days", { n: r })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("settings.default_language")}</Label>
              <Select value={form.default_ui_language || "lt"} onValueChange={set("default_ui_language")}>
                <SelectTrigger data-testid="settings-default-language" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lt" data-testid="settings-language-lt">Lietuvių</SelectItem>
                  <SelectItem value="en" data-testid="settings-language-en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-serif text-lg text-slate-800 mb-4">{t("settings.branding")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              ["invoice", "settings.invoice_logo", "invoice-logo", "logo-preview-invoice", "logo-file-invoice", "logo-upload-invoice"],
              ["symbol", "settings.app_symbol", "symbol", "logo-preview-symbol", "logo-file-symbol", "logo-upload-symbol"],
            ].map(([kind, labelKey, endpoint, previewTestid, fileTestid, btnTestid]) => (
              <div key={kind} className="border border-slate-200 rounded-lg p-4">
                <Label>{t(labelKey)}</Label>
                <div className="mt-2 h-16 flex items-center">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/${endpoint}?v=${logoVer}`}
                    alt=""
                    className="max-h-16 max-w-full object-contain"
                    data-testid={previewTestid}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid={fileTestid}
                    onChange={(e) => setLogoFiles((f) => ({ ...f, [kind]: e.target.files?.[0] || null }))}
                    className="text-xs text-slate-500 file:mr-2 file:rounded-md file:border file:border-slate-200 file:px-2.5 file:py-1.5 file:text-xs file:font-semibold file:bg-white hover:file:bg-slate-50"
                  />
                  <Button
                    type="button"
                    size="sm"
                    data-testid={btnTestid}
                    disabled={!logoFiles[kind] || logoBusy === kind}
                    onClick={() => uploadLogo(kind)}
                    className="bg-slate-900 hover:bg-slate-800 shrink-0"
                  >
                    {logoBusy === kind ? t("common.saving") : t("settings.upload")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">{t("settings.logo_hint")}</p>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            data-testid="settings-save-button"
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800"
          >
            {saving ? t("common.saving") : t("settings.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}
